import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useUser } from './UserContext';

// Color palette based on UID hash
const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-cian-digital/20 text-cian-digital',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

const getAvatarColor = (uid) => {
  if (!uid) return AVATAR_COLORS[0];
  const hash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const sizeClasses = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
};

const Tooltip = ({ text, targetRect }) => {
  if (!targetRect) return null;
  return createPortal(
    <div
      className="fixed px-2 py-1 text-[10px] font-medium text-white bg-gray-800 rounded whitespace-nowrap z-[9999] pointer-events-none"
      style={{
        top: targetRect.top - 6,
        left: targetRect.left + targetRect.width / 2,
        transform: 'translate(-50%, -100%)',
      }}
    >
      {text}
    </div>,
    document.body
  );
};

const BubbleWithTooltip = ({ children, text, className, style }) => {
  const [rect, setRect] = useState(null);

  const handleMouseEnter = useCallback((e) => {
    setRect(e.currentTarget.getBoundingClientRect());
  }, []);

  const handleMouseLeave = useCallback(() => {
    setRect(null);
  }, []);

  return (
    <div
      className={className}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {rect && <Tooltip text={text} targetRect={rect} />}
    </div>
  );
};

const AssignedUsersBubbles = ({
  userIds = [],
  maxDisplay = 3,
  size = 'md',
  onClick,
  className,
}) => {
  const { getInitials, getUserName } = useUser();

  if (!userIds || userIds.length === 0) {
    return (
      <span className="text-gray-400 text-sm">
        N/A
      </span>
    );
  }

  const displayedUsers = userIds.slice(0, maxDisplay);
  const remainingCount = userIds.length - maxDisplay;

  return (
    <div
      className={clsx(
        'flex items-center',
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className
      )}
      onClick={onClick}
    >
      {/* Stacked avatars */}
      <div className="flex -space-x-2">
        {displayedUsers.map((uid, index) => (
          <BubbleWithTooltip
            key={uid}
            text={getUserName(uid)}
            className={clsx(
              'relative inline-flex items-center justify-center rounded-full font-medium',
              'ring-2 ring-white',
              sizeClasses[size],
              getAvatarColor(uid)
            )}
            style={{ zIndex: displayedUsers.length - index }}
          >
            {getInitials(uid)}
          </BubbleWithTooltip>
        ))}

        {/* Overflow indicator */}
        {remainingCount > 0 && (
          <BubbleWithTooltip
            text={userIds.slice(maxDisplay).map(uid => getUserName(uid)).join(', ')}
            className={clsx(
              'relative inline-flex items-center justify-center rounded-full font-medium',
              'ring-2 ring-white bg-gray-200 text-gray-600',
              sizeClasses[size]
            )}
            style={{ zIndex: 0 }}
          >
            +{remainingCount}
          </BubbleWithTooltip>
        )}
      </div>
    </div>
  );
};

export default AssignedUsersBubbles;
